import { useEffect, useState } from "react";
import { Link } from "wouter";
import { leadMagnets } from "@/data/insights";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { trpc } from "@/lib/trpc";

const inputStyle = {
  width: "100%",
  padding: "14px 16px",
  background: "oklch(0.29 0.065 155)",
  border: "1px solid oklch(0.38 0.07 155)",
  borderRadius: "2px",
  color: "oklch(0.94 0.01 80)",
  fontFamily: "'DM Sans', system-ui, sans-serif",
  fontSize: "0.95rem",
  outline: "none",
};

export default function LeadMagnetPage({ guideSlug }: { guideSlug: string }) {
  const guide = leadMagnets.find((resource) => resource.slug === guideSlug)!;
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [downloadPath, setDownloadPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const captureMutation = trpc.leadMagnet.capture.useMutation({
    onSuccess: (result) => {
      setDownloadPath(result.downloadPath);
      setError(null);
    },
    onError: (err) => setError(err.message || "We could not prepare your download. Please try again."),
  });

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content") ?? "";
    const pageUrl = `https://aldersearch.com/insights/${guide.slug}`;

    document.title = `${guide.title} | Alder Search`;
    description?.setAttribute("content", guide.metaDescription);

    const setMeta = (property: string, content: string, attr = "property") => {
      let element = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, property);
        element.setAttribute("data-lead-magnet-meta", "true");
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    setMeta("og:title", `${guide.title} | Alder Search`);
    setMeta("og:description", guide.metaDescription);
    setMeta("og:type", "website");
    setMeta("og:url", pageUrl);
    setMeta("twitter:title", `${guide.title} | Alder Search`);
    setMeta("twitter:description", guide.metaDescription);

    const resourceSchema = document.createElement("script");
    resourceSchema.type = "application/ld+json";
    resourceSchema.id = "lead-magnet-jsonld";
    resourceSchema.appendChild(document.createTextNode(JSON.stringify({
      "@context": "https://schema.org",
      "@type": "DigitalDocument",
      name: guide.title,
      description: guide.metaDescription,
      author: { "@type": "Person", name: "Ryan Ollerenshaw" },
      publisher: { "@type": "Organization", name: "Alder Search", url: "https://aldersearch.com" },
      url: pageUrl,
    })));
    document.head.appendChild(resourceSchema);

    return () => {
      document.title = previousTitle;
      description?.setAttribute("content", previousDescription);
      document.getElementById("lead-magnet-jsonld")?.remove();
      document.querySelectorAll('meta[data-lead-magnet-meta="true"]').forEach((element) => element.remove());
    };
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    captureMutation.mutate({
      email,
      marketingConsent,
      resourceSlug: guide.slug as "why-product-managers-get-rejected" | "why-pm-linkedin-profiles-get-passed-over",
    });
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.008 80)" }}>
      <Navigation />
      <main>
        <section className="pt-36 pb-24 px-6" style={{ background: "oklch(0.22 0.065 155)" }}>
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
            <div>
              <Link
                href="/insights"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontSize: "0.72rem",
                  color: "oklch(0.72 0.09 75)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  textDecoration: "none",
                }}
              >
                ← Back to Insights
              </Link>
              <div className="flex items-center gap-3 mt-9 mb-5">
                <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
                <span
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontSize: "0.68rem",
                    color: "oklch(0.72 0.09 75)",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                  }}
                >
                  Free PDF Guide · Product Hiring, IC to CPO
                </span>
              </div>
              <h1
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 700,
                  fontSize: "clamp(2.4rem, 5.2vw, 4.5rem)",
                  lineHeight: 1.06,
                  letterSpacing: "-0.03em",
                  color: "oklch(0.952 0.012 80)",
                  maxWidth: "720px",
                }}
              >
                {guide.heroTitle} <em style={{ color: "oklch(0.72 0.09 75)" }}>{guide.heroAccent}</em>
              </h1>
              <p
                className="mt-7"
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontWeight: 300,
                  fontSize: "clamp(1.05rem, 1.8vw, 1.22rem)",
                  lineHeight: 1.75,
                  color: "oklch(0.75 0.02 155)",
                  maxWidth: "680px",
                }}
              >
                {guide.heroSummary}
              </p>
            </div>

            <div
              className="p-8 sm:p-10"
              style={{ background: "oklch(0.245 0.065 155)", border: "1px solid oklch(0.62 0.085 75 / 0.45)", borderRadius: "4px" }}
            >
              {downloadPath ? (
                <div className="text-center py-5">
                  <div className="text-3xl mb-5" style={{ color: "oklch(0.72 0.09 75)" }}>✓</div>
                  <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "1.8rem", color: "oklch(0.952 0.012 80)" }}>
                    Your guide is ready.
                  </h2>
                  <p className="mt-3 mb-7" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, lineHeight: 1.7, color: "oklch(0.72 0.02 155)" }}>
                    {guide.downloadReadyCopy}
                  </p>
                  <a
                    href={downloadPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-4"
                    style={{ background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 700, fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none", borderRadius: "2px" }}
                  >
                    Download the guide
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div>
                    <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "1.8rem", color: "oklch(0.952 0.012 80)" }}>
                      Get the guide.
                    </h2>
                    <p className="mt-2" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, lineHeight: 1.7, color: "oklch(0.72 0.02 155)" }}>
                      Enter your email to unlock the PDF and receive relevant product-career insights from Alder Search.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="lead-magnet-email" style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "oklch(0.76 0.02 155)", fontSize: "0.72rem", letterSpacing: "0.09em", textTransform: "uppercase" }}>
                      Email address
                    </label>
                    <input
                      id="lead-magnet-email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="mt-2"
                      style={inputStyle}
                    />
                  </div>
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(event) => setMarketingConsent(event.target.checked)}
                      className="mt-1"
                      style={{ accentColor: "oklch(0.62 0.085 75)" }}
                    />
                    <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.86rem", lineHeight: 1.55, color: "oklch(0.72 0.02 155)" }}>
                      I agree to receive this guide and relevant product-career insights from Alder Search. I can unsubscribe at any time.
                    </span>
                  </label>
                  {error && <p style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: "0.82rem", color: "oklch(0.75 0.14 25)" }}>{error}</p>}
                  <button
                    type="submit"
                    disabled={captureMutation.isPending || !marketingConsent}
                    className="w-full py-4 transition-opacity"
                    style={{ background: "oklch(0.62 0.085 75)", opacity: captureMutation.isPending || !marketingConsent ? 0.55 : 1, color: "oklch(0.18 0.055 155)", fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 700, fontSize: "0.74rem", letterSpacing: "0.1em", textTransform: "uppercase", border: "none", borderRadius: "2px", cursor: captureMutation.isPending || !marketingConsent ? "not-allowed" : "pointer" }}
                  >
                    {captureMutation.isPending ? "Preparing your guide..." : "Unlock the PDF"}
                  </button>
                  <p style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: "0.68rem", lineHeight: 1.55, color: "oklch(0.58 0.02 155)", textAlign: "center" }}>
                    By submitting, you agree to our <Link href="/privacy" style={{ color: "inherit" }}>Privacy Policy</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="px-6 py-24">
          <div className="max-w-5xl mx-auto">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
                <span style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: "0.68rem", color: "oklch(0.45 0.04 155)", letterSpacing: "0.16em", textTransform: "uppercase" }}>Inside the guide</span>
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2rem, 4vw, 3.3rem)", color: "oklch(0.22 0.06 155)", lineHeight: 1.1 }}>
                {guide.insideHeading}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              {guide.insidePoints.map(([title, copy], index) => (
                <article key={title} className="p-7" style={{ background: "white", border: "1px solid oklch(0.88 0.01 80)", borderRadius: "4px" }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.68rem", color: "oklch(0.62 0.085 75)", letterSpacing: "0.1em" }}>0{index + 1}</span>
                  <h3 className="mt-5 mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "1.35rem", color: "oklch(0.22 0.06 155)", lineHeight: 1.25 }}>{title}</h3>
                  <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.95rem", lineHeight: 1.75, color: "oklch(0.45 0.015 80)" }}>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
